import {Stack} from '@commercial-helios/ui/src/components/stack'
import type {ReactNode} from 'react'
import React from 'react'

import useSubmit from '@/hooks/use-submit'
import Button from '@helios/shared/components/button'
import Checkbox from '@helios/shared/components/checkbox'
import Input from '@helios/shared/components/input'
import {useRecaptchaHubspotSubmit} from '@helios/shared/hooks/use-recaptcha-hubspot-submit'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useGoogleReCaptcha} from '@helios/shared/providers/google-recaptcha'
import {useLocale} from '@helios/shared/providers/locale'
import {Text} from '@helios/shared/styles/typography'
import {LifecycleStages} from '@helios/shared/types/marketing-campaigns'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'

import type {State, Data, SetValue} from '../expense-policy-builder'
import {StepWrapper} from '../expense-policy-builder'
import Sidebar from '../sidebar'

interface Props {
    state: State['allDone']
    setValue: SetValue<'allDone'>
    getPDF: () => Promise<string>
    togglePreview: () => void
    data: Data['all_done'][number] & {
        sidebar: Data['sidebar'][number]
    }
    children: ReactNode
}

const AllDone = ({children, state, setValue, getPDF, togglePreview, data}: Props) => {
    const globalStrings = useGlobalStrings()
    const [sharableLink, setSharableLink] = React.useState('')
    const currentLocale = useLocale()
    const {recaptchaHubspotSubmit} = useRecaptchaHubspotSubmit()
    const {loadReCaptcha} = useGoogleReCaptcha()

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        await recaptchaHubspotSubmit(
            {
                email: state.email,
                country: currentLocale.country,
            },
            {
                campaign__updating_: 'Lead Magnet - Expense Policy Builder',
                lifecyclestage: LifecycleStages.Lead,
            },
            () => {
                throw new Error(globalStrings.something_went_wrong)
            }
        )
        const sharableLinkResponse = await getPDF()

        track(TrackingEvent.EPBActioned, {
            action: 'pdf_generated',
        })

        setSharableLink(sharableLinkResponse)
    }

    const {
        handleSubmit: submitEmail,
        error,
        loading: emailLoading,
        clearError,
    } = useSubmit(handleFormSubmit)

    const handleEmailChange = (email: string) => {
        loadReCaptcha()
        clearError()
        setValue('email', email)
    }

    return (
        <StepWrapper>
            <Sidebar
                hideControls
                title={data.title}
                subtitle={data.subtitle}
                onSubmit={submitEmail}
                togglePreview={togglePreview}
                data={data.sidebar}
            >
                {!!sharableLink && (
                    <>
                        <Stack space={10}>
                            <Text variant="paragraph-base">{data.download_pdf}</Text>

                            <Button
                                as="a"
                                href={sharableLink}
                                download
                                onClick={() => {
                                    track(TrackingEvent.EPBActioned, {
                                        action: 'pdf_downloaded',
                                    })
                                }}
                            >
                                {data.download_pdf_cta_text}
                            </Button>
                        </Stack>

                        <Input
                            label={data.sharable_link_cta_text}
                            value={sharableLink}
                            copyable
                            copiedText={globalStrings.copied_to_clipboard}
                        />
                    </>
                )}

                {!sharableLink && (
                    <Stack space={32}>
                        <Stack space={10}>
                            <Input
                                label={data.email}
                                type="email"
                                placeholder={data.email_placeholder}
                                value={state.email}
                                onChange={handleEmailChange}
                                error={error?.message}
                                autoFocus
                            />

                            <Checkbox
                                label={globalStrings.recaptcha_and_privacy_terms}
                                checked={state.consented}
                                onChange={(value) => setValue('consented', value)}
                            />
                        </Stack>

                        <Button
                            type="submit"
                            disabled={!state.consented || !state.email}
                            loading={emailLoading}
                        >
                            {data.submit_text}
                        </Button>
                    </Stack>
                )}
            </Sidebar>

            {children}
        </StepWrapper>
    )
}

export default AllDone
